import {
  type DocumentType,
  getApiDocumentsV1DocumentsPdfCreateMutationOptions,
  getApiDocumentsV1DocumentsRetrieveQueryKey,
  useApiDocumentsV1DocumentsPartialUpdate,
  useApiDocumentsV1DocumentsPdfCreate,
  useApiDocumentsV1DocumentsSendSigningEmailCreate,
  useQueryClient,
} from 'api';
import { createObjectUrl, useNotification } from 'features';
import { useTranslation } from 'i18n';
import { useRef, useState } from 'react';
import { Box, Button, HiddenInput, IconButton, Menu, MenuItem, Stack } from 'ui';
import { DoneOutlined, MoreHorizOutlined } from 'ui/icons';

import { useDocument } from '../../context/DocumentContext';
import { useUploadDocument } from '../../hooks/useUploadDocument';
import { downloadFile } from '../../utils/utils';
import { UpdateAliasDialog } from '..';
import { History } from '../History/History';

export const ActionButtons = () => {
  const { t } = useTranslation('orders');
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [updateAliasDialogOpen, setUpdateAliasDialogOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { document, setFileUrl } = useDocument();
  const [filePath, setFilePath] = useState<string | null>(null);
  const { save, upload, isUploading, isSaving } = useUploadDocument(document.id);
  const { mutate: sendSigningEmail, isPending: isSendSigningEmailPending } =
    useApiDocumentsV1DocumentsSendSigningEmailCreate({
      mutation: {
        onError: () => {
          open({
            message: t('documentGeneration.actionButtons.notifications.failedToSendSigningEmail'),
            type: 'error',
          });
        },
        onSuccess: () => {
          open({ message: t('documentGeneration.actionButtons.notifications.signingEmailSent'), type: 'success' });
        },
      },
    });

  const {
    data: generatedPdfData,
    mutate: generatePdf,
    isPending: isGeneratePdfPending,
    reset: resetGeneratePdf,
  } = useApiDocumentsV1DocumentsPdfCreate({
    mutation: {
      mutationKey: [getApiDocumentsV1DocumentsPdfCreateMutationOptions().mutationKey, 'generate'],
      onError: () => {
        open({ message: t('documentGeneration.actionButtons.notifications.failedToGeneratePdf'), type: 'error' });
      },
      onSuccess: (data, variables) => {
        setFileUrl(data.url);
        queryClient.invalidateQueries({
          queryKey: getApiDocumentsV1DocumentsRetrieveQueryKey(document.id),
        });
        if (variables.data?.save) {
          resetGeneratePdf();
        }
      },
    },
  });

  const openUpdateAliasDialog = () => {
    setUpdateAliasDialogOpen(true);
  };

  const closeUpdateAliasDialog = () => {
    setUpdateAliasDialogOpen(false);
  };

  const openHistory = () => {
    setIsHistoryOpen(true);
  };

  const closeHistoryDialog = () => {
    setIsHistoryOpen(false);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const filePath = await upload(file);
      setFilePath(filePath);
      setFileUrl(createObjectUrl(file));
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownload = () => {
    downloadFile(document.file_url, document.file_name);
  };

  const handleCopySigningLink = () => {
    const signingURl = new URL(`/document/${document.id}/sign`, window.origin);
    navigator.clipboard.writeText(signingURl.toString());
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  };

  const handleSendSigningEmail = () => {
    sendSigningEmail({ documentId: document.id });
  };

  const disableActions = isGeneratePdfPending || isUploading || isSaving;

  const saveDocument = () => {
    // If we don't have a filePath it means that the user didn't upload a new document
    // but they regenerated one. In this case we need to generate the pdf(again) and save it.
    // because we don't receive the file_path from BE.
    if (filePath) {
      save(filePath, document.type as DocumentType).then(() => {
        setFilePath(null);
      });
    } else {
      resetGeneratePdf();
      generatePdf({
        data: { save: true },
        id: Number(document.id),
      });
      queryClient.invalidateQueries({
        queryKey: getApiDocumentsV1DocumentsRetrieveQueryKey(document.id),
      });
    }
  };

  const isNewFile = filePath || generatedPdfData;

  return (
    <Stack
      direction="row"
      useFlexGap
      sx={{
        gap: 1.5,
        justifyContent: 'flex-end',
        my: 1.5,
        px: 1.5,
      }}
    >
      <HiddenInput type="file" ref={fileInputRef} onChange={handleFileSelect} />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Button disabled={!isNewFile} loading={disableActions} onClick={saveDocument}>
          {t('documentGeneration.actionButtons.actions.save')}
        </Button>
        <Button
          variant="secondary"
          loading={isGeneratePdfPending}
          disabled={disableActions}
          onClick={() => {
            generatePdf({
              data: { save: false },
              id: Number(document.id),
            });
          }}
        >
          {t('documentGeneration.actionButtons.actions.regenerate')}
        </Button>
        <Button disabled={disableActions} variant="secondary" onClick={handleUploadClick}>
          {t('documentGeneration.actionButtons.actions.uploadDocument')}
        </Button>
        <Button disabled={disableActions} variant="secondary" onClick={handleDownload}>
          {t('documentGeneration.actionButtons.actions.download')}
        </Button>
        <Button
          disabled={isCopied || disableActions}
          variant="secondary"
          onClick={handleCopySigningLink}
          endIcon={isCopied ? <DoneOutlined /> : null}
        >
          {isCopied
            ? t('documentGeneration.actionButtons.actions.copiedToClipboard')
            : t('documentGeneration.actionButtons.actions.copySigningLink')}
        </Button>
        <Button
          variant="secondary"
          disabled={disableActions}
          loading={isSendSigningEmailPending}
          onClick={handleSendSigningEmail}
        >
          {t('documentGeneration.actionButtons.actions.sendSigningEmail')}
        </Button>
      </Box>
      <More openHistory={openHistory} openUpdateAliasDialog={openUpdateAliasDialog} disabled={disableActions} />
      <History
        open={isHistoryOpen}
        onClose={closeHistoryDialog}
        title={document.alias || document.file_name || t('documentGeneration.history.values.untitledDocument')}
        documentId={document.id}
      />
      <UpdateAliasDialog
        open={updateAliasDialogOpen}
        documentId={document.id}
        alias={document.alias}
        onClose={closeUpdateAliasDialog}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: getApiDocumentsV1DocumentsRetrieveQueryKey(document.id),
          });
        }}
      />
    </Stack>
  );
};

interface MoreProps {
  openHistory: () => void;
  openUpdateAliasDialog: () => void;
  disabled: boolean;
}

const More = ({ openUpdateAliasDialog, disabled }: MoreProps) => {
  const { t } = useTranslation('orders');
  const queryClient = useQueryClient();
  const { document } = useDocument();
  const { mutate: updateDocument } = useApiDocumentsV1DocumentsPartialUpdate({
    mutation: {
      onError: () => {
        open({ message: t('documentGeneration.more.notifications.failedToMarkAsSigned'), type: 'error' });
      },
      onSuccess: () => {
        if (!document) {
          return;
        }

        open({ message: t('documentGeneration.more.notifications.documentMarkedAsSigned'), type: 'success' });
        queryClient.invalidateQueries({
          queryKey: getApiDocumentsV1DocumentsRetrieveQueryKey(document.id),
        });
      },
    },
  });
  const { open } = useNotification();

  const handleMarkAsSigned = () => {
    updateDocument({
      data: {
        manual_fully_signed: true,
      },
      id: document.id,
    });
  };

  return (
    <Menu
      equalMenuWidth={false}
      button={
        <IconButton disabled={disabled}>
          <MoreHorizOutlined titleAccess={t('documentGeneration.more.actions.openActionsMenu')} />
        </IconButton>
      }
    >
      <MenuItem onClick={openUpdateAliasDialog}>{t('documentGeneration.more.actions.updateAlias')}</MenuItem>
      <MenuItem onClick={handleMarkAsSigned} disabled={document?.manual_fully_signed ?? false}>
        {t('documentGeneration.more.actions.markDocumentAsSigned')}
      </MenuItem>
    </Menu>
  );
};
